// User Key Management Service for SAM Encryption
import { apiService } from './api';
import { isDevelopment } from '../config/constants';

// UserKeyService module loaded

export class UserKeyService {
  private static readonly LOCAL_KEY_STORAGE_KEY = 'sam_encryption_key';
  private static readonly LOCAL_KEY_CREATED_KEY = 'sam_key_created';

  private static scopedKeyName(userId: string): string {
    return `${this.LOCAL_KEY_STORAGE_KEY}:${userId.toLowerCase()}`;
  }

  private static scopedCreatedName(userId: string): string {
    return `${this.LOCAL_KEY_CREATED_KEY}:${userId.toLowerCase()}`;
  }

  /** Remove browser-cached keys only — never delete the DynamoDB key. */
  static clearLocalKeys(): void {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k === this.LOCAL_KEY_STORAGE_KEY || k === this.LOCAL_KEY_CREATED_KEY) {
        toRemove.push(k);
      } else if (
        k.startsWith(`${this.LOCAL_KEY_STORAGE_KEY}:`) ||
        k.startsWith(`${this.LOCAL_KEY_CREATED_KEY}:`)
      ) {
        toRemove.push(k);
      }
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  }

  static getLocalKeyString(userId?: string): string | null {
    if (userId) {
      const scoped = localStorage.getItem(this.scopedKeyName(userId));
      if (scoped) return scoped;
    }
    return localStorage.getItem(this.LOCAL_KEY_STORAGE_KEY);
  }

  private static storeLocalKey(userId: string, keyString: string, createdAt?: string): void {
    localStorage.setItem(this.scopedKeyName(userId), keyString);
    localStorage.setItem(this.scopedCreatedName(userId), createdAt || new Date().toISOString());
    // Keep unscoped copies in sync for this user only (legacy readers).
    localStorage.setItem(this.LOCAL_KEY_STORAGE_KEY, keyString);
    localStorage.setItem(this.LOCAL_KEY_CREATED_KEY, createdAt || new Date().toISOString());
  }

  private static async importKeyString(keyString: string): Promise<CryptoKey> {
    const keyData = Uint8Array.from(atob(keyString), (c) => c.charCodeAt(0));
    return crypto.subtle.importKey('raw', keyData, 'AES-GCM', false, ['encrypt', 'decrypt']);
  }

  /**
   * Reload the encryption key from DynamoDB and replace the browser cache.
   * Used when local decrypt fails (stale key from a previous login).
   */
  static async refreshKeyFromServer(): Promise<CryptoKey> {
    const userId = await this.getCurrentUserId();
    const userKey = await apiService.getUserKey(userId);
    if (!userKey?.encryptedKey) {
      throw new Error('Unable to access encryption key. Please check your internet connection and try again.');
    }
    this.storeLocalKey(userId, userKey.encryptedKey, userKey.createdAt);
    return this.importKeyString(userKey.encryptedKey);
  }
  
  /**
   * Generate and store a new encryption key for the user
   */
  static async generateAndStoreKey(): Promise<string> {
    try {
      // Generate a new AES-256-GCM key
      const key = await crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt']
      );
      
      // Export the key to raw format
      const exportedKey = await crypto.subtle.exportKey('raw', key);
      const keyString = btoa(String.fromCharCode(...Array.from(new Uint8Array(exportedKey))));
      
      const userId = await this.getCurrentUserId();
      this.storeLocalKey(userId, keyString);

      // Store in DynamoDB for persistence
      try {
        await apiService.saveUserKey({
          userId: userId,
          encryptedKey: keyString,
          createdAt: new Date().toISOString(),
          lastUsed: new Date().toISOString()
        });
      } catch (error) {
        console.error('❌ Failed to store key in DynamoDB:', error);
        // Continue with localStorage only if DynamoDB fails
      }
      
      return keyString;
    } catch (error) {
      console.error('❌ Error generating encryption key:', error);
      throw new Error('Failed to generate encryption key');
    }
  }
  
  /**
   * Get the user's encryption key (try localStorage first, then DynamoDB)
   */
  static async getKey(): Promise<CryptoKey> {
    try {
      const userId = await this.getCurrentUserId();
      // Prefer a key scoped to this account so a previous login cannot decrypt this one.
      let keyString = localStorage.getItem(this.scopedKeyName(userId));

      if (!keyString) {
        try {
          const userKey = await apiService.getUserKey(userId);

          if (userKey && userKey.encryptedKey) {
            keyString = userKey.encryptedKey;
            this.storeLocalKey(userId, keyString, userKey.createdAt);
            try {
              await apiService.updateUserKeyLastUsed(userId);
            } catch {
              /* non-fatal */
            }
          } else {
            // getUserKey returned null = 404 = Brand new user, no key exists yet
            await this.generateAndStoreKey();
            return this.getKey();
          }
        } catch (dbError) {
          console.error('❌ CRITICAL: Cannot access DynamoDB to retrieve encryption key');
          console.error('   Error:', dbError);
          throw new Error('Unable to access encryption key. Please check your internet connection and try again. If this persists, contact support.');
        }
      }

      if (!keyString) {
        throw new Error('No encryption key found');
      }

      return await this.importKeyString(keyString);
    } catch (error) {
      console.error('❌ CRITICAL ERROR getting encryption key:', error);
      throw error;
    }
  }
  
  /**
   * Check if user has an encryption key
   */
  static async hasKey(): Promise<boolean> {
    try {
      if (this.getLocalKeyString()) {
        return true;
      }
      
      // Check DynamoDB
      const userId = await this.getCurrentUserId();
      const userKey = await apiService.getUserKey(userId);
      return !!(userKey && userKey.encryptedKey);
    } catch (error) {
      console.error('Error checking for key:', error);
      return false;
    }
  }
  
  /**
   * Get key creation date
   */
  static getKeyCreatedDate(): Date | null {
    const created = localStorage.getItem(this.LOCAL_KEY_CREATED_KEY);
    return created ? new Date(created) : null;
  }
  
  /**
   * Clear the encryption key (for testing/reset)
   */
  static async clearKey(): Promise<void> {
    this.clearLocalKeys();
    
    try {
      const userId = await this.getCurrentUserId();
      await apiService.deleteUserKey(userId);
    } catch (error) {
      console.error('Failed to delete key from DynamoDB:', error);
    }
  }
  
  /**
   * Validate that the key is working
   */
  static async validateKey(): Promise<boolean> {
    try {
      const key = await this.getKey();
      const testData = { test: 'validation' };
      const encrypted = await this.encryptTestData(testData, key);
      const decrypted = await this.decryptTestData(encrypted, key);
      return JSON.stringify(testData) === JSON.stringify(decrypted);
    } catch (error) {
      console.error('Key validation failed:', error);
      return false;
    }
  }

  /**
   * Test function to force key generation and storage
   */
  static async testKeyGeneration(): Promise<void> {
    try {
      // Clear any existing key
      this.clearKey();
      
      // Force generate new key
      const keyString = await this.generateAndStoreKey();
      
      // Test retrieval
      const key = await this.getKey();
      
      // Test encryption
      const testData = { test: 'encryption' };
      const encrypted = await this.encryptTestData(testData, key);
      const decrypted = await this.decryptTestData(encrypted, key);
      
      if (JSON.stringify(testData) === JSON.stringify(decrypted)) {
        // Test passed
      } else {
        throw new Error('Encryption/decryption test failed');
      }
      
    } catch (error) {
      console.error('❌ Key generation test failed:', error);
    }
  }
  
  /**
   * Get current user ID
   */
  private static async getCurrentUserId(): Promise<string> {
    try {
      const stored = localStorage.getItem('user_info');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.email) return String(parsed.email);
      }
    } catch {
      /* fall through to whoami */
    }
    try {
      const user = await apiService.whoami();
      return user.email; // Use email as user ID
    } catch (error) {
      console.error('❌ Failed to get current user ID:', error);
      throw new Error('User not authenticated');
    }
  }
  
  /**
   * Test encryption with the current key
   */
  private static async encryptTestData(data: any, key: CryptoKey): Promise<string> {
    const jsonString = JSON.stringify(data);
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(jsonString);
    
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      dataBuffer
    );
    
    const encryptedArray = new Uint8Array(encryptedBuffer);
    const combined = new Uint8Array(iv.length + encryptedArray.length);
    combined.set(iv);
    combined.set(encryptedArray, iv.length);
    
    return btoa(String.fromCharCode(...Array.from(combined)));
  }
  
  /**
   * Test decryption with the current key
   */
  private static async decryptTestData(encryptedData: string, key: CryptoKey): Promise<any> {
    const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));
    
    const iv = combined.slice(0, 12);
    const encryptedArray = combined.slice(12);
    
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encryptedArray
    );
    
    const decoder = new TextDecoder();
    const jsonString = decoder.decode(decryptedBuffer);
    return JSON.parse(jsonString);
  }
}

// Expose test functions globally only in development (never in production)
if (typeof window !== 'undefined' && isDevelopment()) {
  (window as any).testKeyGeneration = UserKeyService.testKeyGeneration.bind(UserKeyService);
  (window as any).clearKey = UserKeyService.clearKey.bind(UserKeyService);
  (window as any).validateKey = UserKeyService.validateKey.bind(UserKeyService);
  (window as any).UserKeyService = UserKeyService;
} 